import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { ResponseAnalyticsService } from 'src/app/shared/services/response-analytics/response-analytics.service';
import { NgStyle, NgIf } from '@angular/common';
import { MSG_CONST } from 'src/app/shared/constants/msg-constants';

Chart.register(...registerables);

@Component({
    selector: 'app-score-question-tab-horizonatal-bar-chart',
    templateUrl: './score-question-tab-horizonatal-bar-chart.component.html',
    styleUrl: './score-question-tab-horizonatal-bar-chart.component.scss',
    imports: [NgStyle, NgIf]
})
export class ScoreQuestionTabHorizonatalBarChartComponent implements OnInit {
  @ViewChild('chartCanvas', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;
  private chartInstance: Chart | any;

  @Input('chartData')
  set chartData(value) {
    this.labels = value?.labels || [];
    this.dataSet = value?.dataset || [];
    this.optionsWithDeleted = value?.options || [];
    this.totalCount = Number(value?.totalCount) || 0;
    this.showChart();
  }

  labels: any = [];
  dataSet: any = [];
  optionsWithDeleted: any[] = [];
  totalCount: number = 0;

  tooltipHeading: string = '';
  tooltipValue: any = '';
  tooltipPercentage: string = '';
  tooltipDeletedMessage: string = '';
  readonly deletedOptionTooltipText = MSG_CONST.DELETED_OPTION_TOOLTIP;

  event: any = {};
  previousTime: any = '';
  tooltipX: any = '';
  tooltipY: any = '';
  tooltipVisible: any = '';


  constructor(
    private responseAnalyticsService: ResponseAnalyticsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.showChart();
  }

  private showChart(): void {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    const optionsWithDeleted = this.optionsWithDeleted;
    const labels = this.labels;
    if (ctx) {
      this.destroyChart();
      const deletedLabelPlugin = {
        id: 'deletedLabelAsterisk',
        afterDraw(chart: Chart) {
          const yScale = chart.scales?.['y'];
          if (!yScale || !optionsWithDeleted?.length || !labels?.length) return;
          const ctx = chart.ctx;
          const scaleOpts = yScale.options as any;
          const font = scaleOpts?.ticks?.font || { size: 12, family: 'Lato' };
          const fontFamily = typeof font === 'object' ? font.family : 'Lato';
          ctx.save();
          ctx.font = `22px ${fontFamily}`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#e53935';
          const asteriskX = yScale.right - 1;
          for (let i = 0; i < labels.length; i++) {
            const opt = optionsWithDeleted[i];
            const isDeleted = opt && (opt.is_deleted === true || opt.is_deleted === 'true' || (opt as any).isDeleted === true);
            if (isDeleted) {
              const y = yScale.getPixelForValue(i);
              ctx.fillText('*', asteriskX, y);
            }
          }
          ctx.restore();
        },
      };
      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: [
            {
              categoryPercentage: 1,
              barPercentage: 0.8,
              data: this.dataSet,
              backgroundColor: ['#FF5722'],
              hoverBackgroundColor: '#FF5722',
              maxBarThickness: 34,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              grid: {
                offset: true,
                display: true,
                lineWidth: 0,
                drawOnChartArea: false,
                color: function (context) {
                  return 'rgba(255, 255, 255, 0)';
                },
              },
              ticks: {
                padding: 0,
                callback: function(value: any, index: number, values: any) {
                  let label: string = typeof value === 'string' ? value : String(this.getLabelForValue(value));
                  const maxCharsPerLine: number = 60;
                  const words: string[] = label.split(' '); 
                  const lines: string[] = []; 
                  let currentLine: string = ''; 
                  words.forEach((word: string) => {
                      if ((currentLine + word).length > maxCharsPerLine) {
                          lines.push(currentLine.trim()); 
                          currentLine = word + ' '; 
                      } else {
                          currentLine += word + ' '; 
                      }
                  });               
                  if (currentLine) {
                      lines.push(currentLine.trim()); 
                  }
                  return lines; 
                }, 
              },
              beginAtZero: true,
            },
            x: {
              ticks: {
                maxTicksLimit: 6,
                stepSize: 1
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
        },
        plugins: [deletedLabelPlugin],
      });
      this.chartInstance.canvas.addEventListener('mousemove', (event) => {
        const chartArea = this.chartInstance.chartArea;
        const mouseX = event.offsetX;
        const mouseY = event.offsetY;
        if (mouseX >= chartArea.left && mouseX <= chartArea.right && mouseY >= chartArea.top && mouseY <= chartArea.bottom) {
          const barHeight = (chartArea.bottom - chartArea.top) / this.labels.length;
          const nearestIndex = Math.floor((mouseY - chartArea.top) / barHeight);
          if (nearestIndex >= 0 && nearestIndex < this.labels.length) {
            const obj = {
              index: nearestIndex,
              time: new Date().getTime()
            };
            this.event = obj;
            this.tooltipVisible = 'block';
            this.cdr.markForCheck();
          }
        } else {
          this.tooltipVisible = 'none';
          this.cdr.markForCheck();
        }
      });
    }
  }

  @HostListener('document:mousemove', ['$event'])
      onMouseMove(event: MouseEvent): void {
        const container = this.chartCanvas?.nativeElement?.parentElement;
        const rect = container?.getBoundingClientRect?.();
        if (rect) {
          const relX = event.clientX - rect.left;
          const relY = event.clientY - rect.top;
          const offset = 15;
          this.tooltipX = relX + offset;
          this.tooltipY = relY + offset;
        } else {
          this.tooltipX = event.clientX + 15;
          this.tooltipY = event.clientY + 10;
        }
        if (this.previousTime !== this.event?.time && this.event?.time) {
          this.previousTime = this.event?.time;
          this.tooltipVisible = 'block';
        } else {
          this.tooltipVisible = 'none';
        }
        this.tooltipHeading = this.labels[this.event?.index];
        const count = Number(this.dataSet[this.event?.index]) || 0;
        this.tooltipValue = this.responseAnalyticsService.formatNumber(count);
        this.tooltipPercentage = this.formatTooltipPercentage(count);
        const opt = this.optionsWithDeleted?.[this.event?.index];
        this.tooltipDeletedMessage = opt?.is_deleted ? this.deletedOptionTooltipText : '';
        this.cdr.markForCheck();
      }

  private formatTooltipPercentage(count: number): string {
    if (!this.totalCount || this.totalCount <= 0) {
      return '0%';
    }
    const pct = (count / this.totalCount) * 100;
    const formatted = pct.toFixed(1).endsWith('.0') ? String(Math.floor(pct)) : pct.toFixed(1);
    return `${formatted}%`;
  }
  

  private destroyChart(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }
}